import { ErrorBoundary } from "@sentry/react";
import { ApiKeysPage } from "pages/developer/apps/api-keys-page";
import { EditApiKey } from "pages/developer/apps/edit-api-key";
import { BillingDetails as OrgBillingDetails } from "components/settings/organization/billing-details/billing-details";
import { GeneralSettings } from "components/settings/organization/general-settings/general-settings";
import OrgMemberDetails from "components/settings/organization/members/member-details";
import { OrganisationMembers } from "components/settings/organization/members/members-page";
import { ManageSubscription } from "components/settings/organization/subscription/organisation-manage-subscription";
import { Subscription as OrgSubscription } from "components/settings/organization/subscription/subscription";
import { LegalPage } from "@/pages/legal/legal";
import { UserInvitations } from "components/settings/user/pending-invitations/user-invitations";
import { UserProfile } from "components/settings/user/profile/user-profile";
import { UserSecurity } from "components/settings/user/security/user-security";
import { ErrorFallback } from "components/shared/error-fallback";
import { TestError } from "components/test";
import { UsagePage } from "pages/geoscape-hub/usage/usage-page";
import { getActiveOrganisation, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { fetchDemos } from "data/queries-common";
import { BatchPage } from "pages/geoscape-batch/batch-page";
import { CreateBatchPage } from "pages/geoscape-batch/create-batch-steps/create-batch-page";
import { CatalogueEntry } from "pages/geoscape-data/catalogue/catalogue-entry/catalouge-entry";
import { CataloguePage } from "pages/geoscape-data/catalogue/catalogue-page";
import { CustomDownloadPage } from "pages/geoscape-data/custom-download/custom-page";
import { DataPage } from "pages/geoscape-data/dashboard/dashboard-page";
import { DownloadPage } from "pages/geoscape-data/download/download-page";
import { ExplorerPage } from "pages/geoscape-data/explorer/explorer-page";
import { ClipManagementPage } from "pages/geoscape-data/clip/clip-management/clip-management-page";
import { TailorPage } from "pages/geoscape-data/tailor/tailor-page";
import {
  DataDemos,
  DemoItem,
  DemosPage,
} from "pages/geoscape-demos/demo-page/demos-page";
import { DocsPage } from "pages/geoscape-docs/docs-page";
import { HubPage } from "pages/geoscape-hub/dashboard/dashboard-page";
import { WhatsNewPage } from "pages/geoscape-hub/whats-new/whats-new-page";
import { ArticleDetailPage } from "pages/geoscape-hub/whats-new/article-detail-page";
import { Suspense, lazy } from "react";
import { useQuery } from "@tanstack/react-query";
import { Redirect, Route, Switch, useLocation } from "react-router";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";
import { DemoRoutes } from "./demo-routes";
import { LayoutRoute } from "./layout-route";
import { StandardPageLayout } from "@/components/layouts/standard-page-layout";

const OrganisationSearch = lazy(
  () => import("pages/geoscape-admin/organisation/organisation-search"),
);
const AdminUsers = lazy(
  () => import("pages/geoscape-admin/users/search/admin-users-search"),
);
const AdminDemos = lazy(
  () => import("pages/geoscape-admin/demo-management/admin-demos"),
);
const AdminEditDemo = lazy(
  () => import("pages/geoscape-admin/demo-management/admin-edit-demo"),
);
const AdminUserDetails = lazy(
  () => import("pages/geoscape-admin/users/user-details/admin-user-details"),
);

const AdminMemberDetails = lazy(
  () =>
    import("pages/geoscape-admin/organisation/members/admin-member-details"),
);

const AdminOrganisationTabs = lazy(
  () => import("pages/geoscape-admin/organisation/tabs/organisation-tabs"),
);
const AdminRoutes = lazy(() => import("routes/admin-routes"));
const SalesToolsRoutes = lazy(() => import("routes/sales-tools-routes"));

const RuralSchedulePage = lazy(
  () => import("pages/sales-tools/rural-schedule/rural-schedule-page"),
);

const OpportunitiesPage = lazy(
  () => import("pages/sales-tools/opportunities/opportunities-page"),
);
const OpportunityCreatePage = lazy(
  () => import("pages/sales-tools/opportunities/pages/opportunity-create-page"),
);
const OpportunityDetailPage = lazy(
  () => import("pages/sales-tools/opportunities/pages/opportunity-detail-page"),
);
const OpportunityEditPage = lazy(
  () => import("pages/sales-tools/opportunities/pages/opportunity-edit-page"),
);

const MangeReleasePage = lazy(
  () => import("pages/geoscape-admin/manage-release/manage-release-page"),
);
const NewRelease = lazy(
  () => import("pages/geoscape-admin/manage-release/new-release/new-release"),
);
const ViewAllDatasets = lazy(
  () =>
    import("pages/geoscape-admin/manage-release/view-all/view-all-datasets"),
);
const AdminDatasets = lazy(
  () => import("pages/geoscape-admin/datasets/admin-datasets-page"),
);
const AdminClipReporting = lazy(
  () => import("pages/geoscape-admin/reporting/clip/admin-clip-reporting"),
);
const AdminCustomDatasetsSearch = lazy(
  () =>
    import("pages/geoscape-admin/custom-datasets/admin-custom-datasets-search"),
);

export const RouteSwitch = () => {
  const location = useLocation();
  const errorKey = location.pathname;

  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);

  const demosQuery = useQuery({
    queryKey: [QueryKeys.demosKey, identityId],
    queryFn: () => fetchDemos(),
  });

  const getDemoItem = (demoId: string): DemoItem | undefined => {
    return DataDemos.find((item) => item.id === demoId);
  };

  return (
    <ErrorBoundary key={errorKey} fallback={<ErrorFallback />}>
      <Suspense fallback={<div>Loading...</div>}>
        <Switch>
          <Route exact path={Routes.hub} component={HubPage} />
          <Route exact path={Routes.article} component={ArticleDetailPage} />
          <Route exact path={Routes.whatsNew} component={WhatsNewPage} />

          {/* DEVELOPER */}
          <LayoutRoute
            exact
            path={Routes.developerApiKeys}
            component={ApiKeysPage}
          />
          <Route exact path={Routes.developerUsage} component={UsagePage} />
          <LayoutRoute
            exact
            path={Routes.developerApiKeysEditKey}
            component={EditApiKey}
          />

          <Route exact path={Routes.docsPage} component={DocsPage} />
          {/* DATA */}

          <LayoutRoute exact path={Routes.dataDashboard} component={DataPage} />
          <Route exact path={Routes.dataCatalogue} component={CataloguePage} />

          <LayoutRoute
            exact
            path={Routes.demosPage}
            component={DemosPage}
          />
          <LayoutRoute
            exact
            path={Routes.dataCatalogueEntry}
            component={CatalogueEntry}
          />
          <Route exact path={Routes.dataExplorer} component={ExplorerPage} />

          {/** Demos */}
          {DataDemos.map((item: DemoItem, index: number) => (
            <DemoRoutes
              key={`${item.id}-${index}]`}
              exact
              path={item.path}
              component={getDemoItem(item.id)?.component}
            />
          ))}

          <Route path={Routes.clipUi} component={ExplorerPage} />
          <Route
            exact
            path={Routes.clipManagement}
            component={ClipManagementPage}
          />

          <LayoutRoute
            exact
            path={Routes.dataCustomDownload}
            component={CustomDownloadPage}
          />

          <LayoutRoute
            exact
            path={Routes.dataTailor}
            component={TailorPage}
          />
          <LayoutRoute
            exact
            path={Routes.dataDownload}
            component={DownloadPage}
          />

          {/* Batches */}
          <LayoutRoute
            exact
            path={Routes.batchBatches}
            component={BatchPage}
          />

          <LayoutRoute
            exact
            path={Routes.batchCreateBatch}
            component={CreateBatchPage}
          />
          <LayoutRoute
            exact
            path={Routes.userPendingInvitations}
            component={UserInvitations}
          />
          <LayoutRoute
            exact
            path={Routes.userSecurity}
            component={UserSecurity}
          />
          <LayoutRoute
            exact
            path={Routes.userGeneralSettings}
            component={UserProfile}
          />

          <LayoutRoute exact path={Routes.legal} component={LegalPage} />
          {/* Secret endpoint for testing error handling */}
          <LayoutRoute
            exact
            path={Routes.testError}
            component={TestError}
          />

          {/*  Org Routes */}
          <OrgRoutes path={Routes.orgGeneralSettings}>
            <GeneralSettings />
          </OrgRoutes>
          <OrgRoutes path={Routes.orgMembers}>
            <OrganisationMembers />
          </OrgRoutes>
          <OrgRoutes path={Routes.orgSubscription}>
            <OrgSubscription />
          </OrgRoutes>
          <OrgRoutes path={Routes.orgFeatureComparisonTable}>
            <OrgSubscription />
          </OrgRoutes>
          <OrgRoutes path={Routes.orgFeatureCostTable}>
            <OrgSubscription />
          </OrgRoutes>

          <OrgRoutes path={Routes.orgBillingDetails}>
            <OrgBillingDetails />
          </OrgRoutes>

          <OrgRoutes path={Routes.orgManageSubscription}>
            <ManageSubscription />
          </OrgRoutes>

          <OrgRoutes path={Routes.orgMemberDetails}>
            <OrgMemberDetails />
          </OrgRoutes>

          {/* Admin Routes */}
          <AdminRoutes exact path={Routes.adminOrganisations}>
            <OrganisationSearch />
          </AdminRoutes>

          <AdminRoutes exact path={Routes.adminDemoManagement}>
            <AdminDemos />
          </AdminRoutes>

          <AdminRoutes exact path={Routes.adminEditDemo}>
            <AdminEditDemo />
          </AdminRoutes>

          <AdminRoutes exact path={Routes.adminUsers}>
            <AdminUsers />
          </AdminRoutes>

          <AdminRoutes exact path={Routes.adminUserDetails}>
            <AdminUserDetails />
          </AdminRoutes>

          <AdminRoutes exact path={Routes.AdminMemberDetails}>
            <AdminMemberDetails />
          </AdminRoutes>
          <AdminRoutes exact path={Routes.adminReleases}>
            <MangeReleasePage />
          </AdminRoutes>

          <AdminRoutes exact path={Routes.adminEditRelease}>
            <Route
              exact
              path={Routes.adminEditRelease}
              component={NewRelease}
            />
          </AdminRoutes>

          <AdminRoutes exact path={Routes.adminReleaseDatasets}>
            <Route
              exact
              path={Routes.adminReleaseDatasets}
              component={ViewAllDatasets}
            />
          </AdminRoutes>

          <AdminRoutes exact path={Routes.adminDatasets}>
            <AdminDatasets />
          </AdminRoutes>

          <AdminRoutes exact path={Routes.adminClipReporting}>
            <AdminClipReporting />
          </AdminRoutes>

          <AdminRoutes exact path={Routes.adminViewCustomDatasets}>
            <AdminCustomDatasetsSearch />
          </AdminRoutes>

          <AdminRoutes exact path={Routes.adminOrganisationDetails}>
            <Route
              path={Routes.adminOrganisationDetails}
              component={AdminOrganisationTabs}
            />
          </AdminRoutes>

          <AdminRoutes exact path={Routes.adminOrganisationDetailsSubTab}>
            <Route
              path={Routes.adminOrganisationDetailsSubTab}
              component={AdminOrganisationTabs}
            />
          </AdminRoutes>

          <AdminRoutes exact path={Routes.adminOrganisationDetailsSubTabEdit}>
            <Route
              path={Routes.adminOrganisationDetailsSubTabEdit}
              component={AdminOrganisationTabs}
            />
          </AdminRoutes>

          {/* Sales Tools Routes */}
          <SalesToolsRoutes exact path={Routes.salesToolsRuralSchedule}>
            <RuralSchedulePage />
          </SalesToolsRoutes>

          <SalesToolsRoutes exact path={Routes.salesToolsOpportunities}>
            <OpportunitiesPage />
          </SalesToolsRoutes>
          <SalesToolsRoutes exact path={Routes.salesToolsOpportunitiesCreate}>
            <OpportunityCreatePage />
          </SalesToolsRoutes>
          <SalesToolsRoutes exact path={Routes.salesToolsOpportunitiesEdit}>
            <OpportunityEditPage />
          </SalesToolsRoutes>
          <SalesToolsRoutes exact path={Routes.salesToolsOpportunitiesDetail}>
            <OpportunityDetailPage />
          </SalesToolsRoutes>

          <Route path="*">
            <Redirect to={Routes.hub} />
          </Route>
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
};

const OrgRoutes = ({ children, ...rest }: any) => {
  const [identity] = useIdentity();

  return (
    <Route
      {...rest}
      render={({ location }) =>
        getActiveOrganisation(identity) ? (
          <StandardPageLayout>{children}</StandardPageLayout>
        ) : (
          <Redirect
            to={{
              pathname: "/",
              state: { from: location },
            }}
          />
        )
      }
    />
  );
};
